import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import api, { getMedicalPin, setMedicalPin } from "@/lib/api";
import { useAreaOpen } from "@/hooks/useAreaOpen";
import { useAuth } from "@/context/AuthContext";
import { canAccessArea } from "@/lib/areas";
import AreaDenied from "@/components/AreaDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, Heartbeat } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

export function useMedicalUnlocked() {
  const { user } = useAuth();
  const noPin = user && typeof user === "object" && user.org && user.org.pins_required && user.org.pins_required.medical === false;
  const [unlocked, setUnlocked] = useState(!!getMedicalPin());
  useEffect(() => {
    const on = () => setUnlocked(!!getMedicalPin());
    window.addEventListener("medical-locked", on);
    window.addEventListener("medical-unlocked", on);
    return () => { window.removeEventListener("medical-locked", on); window.removeEventListener("medical-unlocked", on); };
  }, []);
  return unlocked || !!noPin;
}

export default function MedicalGate() {
  const { user, refreshMe } = useAuth();
  const { tr } = useLang();
  const unlocked = useMedicalUnlocked();
  const areaState = useAreaOpen("medical");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [gdpr, setGdpr] = useState(false);

  const needAck = !(user && typeof user === "object" && user.gdpr_medical_ack_at);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    if (needAck && !gdpr) { setError(tr("Devi confermare l'adempimento GDPR (art. 9) per accedere.", "You must confirm GDPR (Art. 9) compliance to proceed.")); return; }
    setBusy(true); setError("");
    try {
      if (needAck) {
        await api.post("/analysis/medical/gdpr-ack");
        try { await refreshMe(); } catch (e2) { /* ignore */ }
      }
      await api.post("/analysis/medical/unlock", { pin: pin.trim() });
      setMedicalPin(pin.trim());
      setPin("");
    } catch (err) {
      setError(err.response?.status === 403 ? tr("PIN errato. Riprova.", "Wrong PIN. Please try again.") : tr("Impossibile verificare il PIN.", "Unable to verify the PIN."));
    } finally { setBusy(false); }
  };

  if (areaState === "checking") return null;
  if (!canAccessArea(user, "medical")) return <AreaDenied area="medical" />;
  if (unlocked || areaState === "open") return <Outlet />;

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="medical-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8 space-y-6 relative overflow-hidden">
        <Heartbeat size={140} weight="fill" className="absolute -right-8 -bottom-8 text-red-500/10" />
        <div className="relative">
          <div className="w-12 h-12 rounded-sm bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-400 mb-4"><LockKey size={26} weight="fill" /></div>
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-red-400">{tr("Riservato · area medica", "Confidential · medical")}</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mt-1">{tr("Area Medica", "Medical Health")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{tr("Inserisci il PIN medico per accedere alle cartelle sanitarie delle calciatrici.", "Enter the medical PIN to access player health records.")}</p>
        </div>
        <div className="space-y-2 relative">
          <Input data-testid="medical-pin-input" type="password" inputMode="numeric" autoFocus value={pin}
            onChange={(e) => setPin(e.target.value)} placeholder="PIN" className="text-center text-2xl tracking-[0.5em] font-display h-14" />
          {error && <p className="text-sm text-red-400" data-testid="medical-pin-error">{error}</p>}
        </div>
        {needAck && (
          <label className="flex items-start gap-3 text-xs text-muted-foreground leading-relaxed cursor-pointer relative" data-testid="gdpr-ack-label">
            <input type="checkbox" data-testid="gdpr-ack-checkbox" checked={gdpr} onChange={(e) => setGdpr(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-red-500 shrink-0" />
            <span>{tr(
              "Confermo di accedere a dati sanitari, categoria particolare ai sensi dell'art. 9 del Reg. UE 2016/679 (GDPR), nel rispetto degli obblighi di legge e di riservatezza. L'accesso viene registrato a fini di audit.",
              "I confirm I am accessing health data, a special category under Art. 9 of EU Reg. 2016/679 (GDPR), in compliance with legal and confidentiality obligations. Access is logged for audit purposes."
            )}</span>
          </label>
        )}
        <Button type="submit" disabled={busy || (needAck && !gdpr)} data-testid="medical-pin-submit" className="w-full font-display uppercase tracking-wide h-11">{busy ? tr("Verifica…", "Checking…") : tr("Sblocca", "Unlock")}</Button>
      </form>
    </div>
  );
}
